'use client';

import { DollarSign, MousePointerClick, Mail, Phone, FileText, Home, Banknote } from 'lucide-react';
import { FONT_TEXT } from '@/app/fonts';
import { ShareableChart } from '@/components/blog/ShareableChart';

const stages = [
  { label: 'Ad Spend', value: '$5,000/mo', dropoff: null, Icon: DollarSign, color: '#294bfd' },
  { label: 'Clicks', value: '350–500', dropoff: '$10–$14 blended CPC', Icon: MousePointerClick, color: '#294bfd' },
  { label: 'Leads', value: '85–125', dropoff: 'About 25% LP conversion*', Icon: Mail, color: '#294bfd' },
  { label: 'Contacts', value: '50–75', dropoff: '60% contact rate', Icon: Phone, color: '#ff34a7' },
  { label: 'Applications', value: '15–25', dropoff: '33% of contacts apply', Icon: FileText, color: '#ff34a7' },
  { label: 'Funded Loans', value: '3–6', dropoff: '2–5% lead-to-funded', Icon: Home, color: '#10b981' },
  { label: 'Revenue', value: '$12K–$24K', dropoff: '~$4K avg/loan', Icon: Banknote, color: '#10b981' },
];

export function MortgageFunnelFlow() {
  return (
    <ShareableChart title="Mortgage Lead Generation Funnel">
      <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: FONT_TEXT }}>
        The Mortgage Lead Generation Funnel
      </h3>
      <p className="text-sm text-gray-500 mb-6" style={{ fontFamily: FONT_TEXT }}>
        Based on $5,000/mo blended ad spend (Google + Facebook) — your numbers will vary based on market, systems, and follow-up
      </p>
      
      <div className="flex flex-col gap-1">
        {stages.map((stage, i) => (
          <div key={stage.label} className="flex items-center gap-3">
            {/* Bar that gets narrower as funnel progresses */}
            <div 
              className="flex items-center justify-between px-4 py-3 rounded-md text-white font-medium"
              style={{ 
                backgroundColor: stage.color,
                width: `${100 - (i * 10)}%`,
                minWidth: '200px',
                fontFamily: FONT_TEXT,
                transition: 'width 0.3s ease',
              }}
            >
              <span className="text-sm flex items-center gap-2">
                <stage.Icon size={16} aria-hidden="true" className="shrink-0" />
                <span>{stage.label}</span>
              </span>
              <span className="text-sm font-bold">{stage.value}</span>
            </div>
            {/* Drop-off annotation */}
            {stage.dropoff && stage.dropoff !== '→' && (
              <span className="text-xs text-gray-400 whitespace-nowrap" style={{ fontFamily: FONT_TEXT }}>
                {stage.dropoff}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 rounded-lg border-l-4" style={{ borderColor: '#ff34a7', backgroundColor: '#fff5fb' }}>
        <p className="text-sm text-gray-700" style={{ fontFamily: FONT_TEXT }}>
          <strong>Key insight:</strong> Most LOs focus on the top (more leads) when the biggest ROI gains are in the middle — contact rates and application conversion. Improving contact rate from 40% to 60% doubles your output without spending another dollar on ads.
        </p>
      </div>

      <div className="mt-3 p-3 rounded-lg border-l-4" style={{ borderColor: '#294bfd', backgroundColor: '#f0f4ff' }}>
        <p className="text-sm text-gray-700" style={{ fontFamily: FONT_TEXT }}>
          <strong>*About 25% of visitors become leads</strong>, the conservative end of the 25–35% a well-optimized rebel iQ landing page achieves. Point the same ads at a typical website and under 2% become leads. Higher conversion means more leads from the same ad spend.
        </p>
      </div>

      <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: FONT_TEXT }}>
        Source: leadpops.com · Based on rebel iQ landing page performance · Typical website: under 2% of visitors become leads
      </p>
    </ShareableChart>
  );
}
